// src/actions/Booking.js
/*
 * Project: BookingEngine
 * Module: actions/Booking.js
 * vim:ft=javascript.jsx:
 */

import moment from 'moment';

import {
  refreshNights, searchAvailability, setCheckIn, setCheckOut, setDestination,
} from 'bookingform';

const { AdyenCheckout, Dropin } = window.AdyenWeb;
import * as signalR from '@microsoft/signalr';
import { apiGet, apiPost } from '../api';

import { emptyShoppingCart, emptyShoppingCartEvent } from './Cart';
import { confirmFormState } from '../reducers/Booking';
import { FREE_MODE_DISTRIBUTION, FREE_MODE_ID, MULTIPLE_ROOMS } from '../constants';
import { chooseRoomEvent, roomListLoadedEvent } from '../events/Room';
import { isCallCenterUser } from '../utils/Generic';
import { distributionToParam, toUseWithAges } from '../utils/Room';
import { addEventToDataLayer, buildExtraAnalyticsParams } from '../utils/Analytics';
import { bookingCreatedEvent } from '../events/Booking';
import { getMemberInfo } from './Loyalty';
import { store } from '../Store';

const sortByPriceFrom = (a, b) => (a.price_from - b.price_from || 1);

/**
 * BookingEngine constants
 */

export const BOOKING_STEP = Object.freeze({
  INITIAL: 'INITIAL',
  CHOOSE: 'CHOOSE',
  CONFIRM: 'CONFIRM',
  NO_AVAIL_STEP: 'NO_AVAIL_STEP',
});

export const DEVICES = Object.freeze({
  TABLETV: 1,
  TABLETH: 2,
  DESKTOP: 3,
  MOBILE: 4,
});

const getDeviceName = (device) => {
  switch (device) {
    case 3:
      return 'DESKTOP';
    case 4:
      return 'MOBILE';
    default:
      return 'TABLET';
  }
};

/**
 *  Action definitions - exported
 */

/* Sets the device which is being used by the user */
export const setDevice = (device) => ({
  type: 'BOOKING_SET_DEVICE', device,
});

/* Sets the current step within the Booking process */
export const setStep = (step) => ({
  type: 'BOOKING_SET_STEP', step,
});

/* Sets the number of days that is required as a minimum stay for the last availability */
export const setAvailabilityMinStay = (minStay) => ({
  type: 'BOOKING_SET_AVAIL_MIN_STAY', minStay,
});

/* Sets the selected sort criteria */
export const setFilterOrder = (value) => ({
  type: 'BOOKING_SET_FILTER_ORDER', value,
});

/* Sets the selected category filter criteria */
export const setFilterCategory = (value) => ({
  type: 'BOOKING_SET_FILTER_CATEGORIES', value,
});

/* Sets the agentCode */
export const setAgentCode = (value) => ({
  type: 'BOOKING_SET_AGENT_CODE', value,
});

/* Sets the agentSell */
export const setAgentSell = (value) => ({
  type: 'BOOKING_SET_AGENT_SELL', value,
});

/* Sets the rateType */
export const setRateType = (value) => ({
  type: 'BOOKING_SET_RATE_TYPE', value,
});

/* Sets the agentUseCoins */
export const setAgentUseCoins = (value) => ({
  type: 'BOOKING_SET_AGENT_USE_COINS', value,
});

/* Sets the user currency that is used to display the booking amounts */
export const setFilterCurrency = (value) => ({
  type: 'BOOKING_SET_FILTER_CURRENCY', value,
});

/* Sets the user payment currency that is used to pay the booking */
export const setPaymentCurrency = (value) => ({
  type: 'BOOKING_SET_PAYMENT_CURRENCY', value,
});

/* Sets the list of available currencies for the current search */
export const setCurrencies = (currencies) => ({
  type: 'BOOKING_SET_CURRENCIES', currencies,
});

/* Sets the room that is being displayed within the Room details pop-up */
export const setSelectedRoomDetail = (room) => ({
  type: 'BOOKING_SET_SELECTED_ROOM_DETAIL', room,
});

/* Sets the number of room which is to be shown (when in two-rooms mode) */
export const setMultipleRoomStep = (roomStep) => ({
  type: 'BOOKING_MULTIPLE_ROOM_STEP', roomStep,
});

/* Si estamos en la página de gracias, después de traer los datos de la reserva, se setea
esta variable para indicar que estamos editando la reserva, para que cuando vayamos a /rooms/ se
inserten los cuartos que estaban reservados en la booking */
export const setIsEditingBooking = (isEditing) => ({
  type: 'IS_EDITING_BOOKING', isEditing,
});

export const setClientIpCountryCode = (clientIpCountryCode) => ({
  type: 'CLIENT_IP_COUNTRY_CODE', clientIpCountryCode,
});

export const setCoinsUsedInEditingBooking = (coins) => ({
  type: 'COINS_USED_IN_EDITING_BOOKING', coins,
});

export const setDingusCustomerCodeUsedInEditingBooking = (customerCode) => ({
  type: 'DINGUS_CUSTOMER_CODE_USED_IN_EDITING_BOOKING', customerCode,
});

export const setEditBookingToken = (token) => ({
  type: 'EDIT_BOOKING_TOKEN', token,
});

export const setEditBooking = (booking) => ({
  type: 'EDIT_BOOKING', booking,
});

export const setShowEditBookingContactInformation = (show) => ({
  type: 'SHOW_EDIT_BOOKING_CONTACT_INFORMATION', show,
});

export const setShowSomeRoomsNotAvailableOnNewDates = (show) => ({
  type: 'SHOW_SOME_ROOMS_NOT_AVAILABLE_ON_NEW_DATES', show,
});

// Se usa para controlar cuando se ha actualizado la información de la reserva
// usando el popup. Entonces, es para que el componente padre
// se actualice si se actualizó la información de la reserva
export const setBookingContactInformationHasBeenEdited = (edited) => ({
  type: 'BOOKING_CONTACT_INFORMATION_HAS_BEEN_EDITED', edited,
});

/* Sets the locator of the booking that was loaded or created */
export const setBookLocator = (locator) => ({
  type: 'BOOKING_SET_BOOK_LOCATOR', locator,
});

/* Sets the customer's email of the booking that was loaded or created */
export const setBookEmail = (email) => ({
  type: 'BOOKING_SET_BOOK_EMAIL', email,
});

/* Sets the information of the last availability search */
export const setLastRequestedInfo = (info) => ({
  type: 'BOOKING_SET_LAST_REQUESTED_INFO', info,
});

/* Sets the hotel which has been selected within the availability process */
export const setHotel = (hotel) => ({
  type: 'BOOKING_SET_HOTEL', hotel,
});

/* Sets the state of the Confirmation form */
export const setConfirmFormState = (state) => ({
  type: 'BOOKING_SET_CONFIRMFORM_STATE', state,
});

export const setConfirmErrorState = (state) => ({
  type: 'BOOKING_SET_CONFIRMERROR_STATE', state,
});

/**
 *  Action definitions - internal
 */

/* Sets the error message produced by an availability search */
const setAvailError = (error) => ({
  type: 'BOOKING_SET_AVAIL_ERROR', error,
});

const getRateBoards = (selection) => {
  if (!selection || !selection.room || !selection.room.rates || !selection.rateCode) {
    return null;
  }
  const rate = selection.room.rates.find((r) => r.code === selection.rateCode);
  return rate.boards;
};

const findBoard = (boardList, boardCode) => boardList.find((b) => b.code === boardCode);

const getBoardsAmounts = (selection1, selection2, selection3, selection4) => {
  const boardAmounts = {};

  const rate1Boards = selection1 ? getRateBoards(selection1) : null;
  const rate2Boards = selection2 ? getRateBoards(selection2) : null;
  const rate3Boards = selection3 ? getRateBoards(selection3) : null;
  const rate4Boards = selection4 ? getRateBoards(selection4) : null;

  // Buscar el primer rateBoards válido
  const baseRateBoards = rate1Boards || rate2Boards || rate3Boards || rate4Boards;

  if (!baseRateBoards) {
    return boardAmounts; // Retornar vacío si no hay ningún board disponible
  }

  baseRateBoards.forEach((board) => {
    const board1 = rate1Boards ? findBoard(rate1Boards, board.code) : null;
    const board2 = rate2Boards ? findBoard(rate2Boards, board.code) : null;
    const board3 = rate3Boards ? findBoard(rate3Boards, board.code) : null;
    const board4 = rate4Boards ? findBoard(rate4Boards, board.code) : null;

    const currentBoardAmounts = {};
    const currencyKeys = board.amount ? Object.keys(board.amount) : [];

    currencyKeys.forEach((currencyCode) => {
      const amount1 = board1?.amount?.[currencyCode]?.amount || 0;
      const amount2 = board2?.amount?.[currencyCode]?.amount || 0;
      const amount3 = board3?.amount?.[currencyCode]?.amount || 0;
      const amount4 = board4?.amount?.[currencyCode]?.amount || 0;

      currentBoardAmounts[currencyCode] = amount1 + amount2 + amount3 + amount4;
    });

    boardAmounts[board.code] = currentBoardAmounts;
  });

  return boardAmounts;
};

const addAmounts = (selection1, selection2, selection3, selection4) => {
  const rate1Boards = getRateBoards(selection1);
  const rate2Boards = getRateBoards(selection2);
  const rate3Boards = getRateBoards(selection3);
  const rate4Boards = getRateBoards(selection4);

  const board1 = selection1 ? findBoard(rate1Boards, selection1.boardCode) : null;
  const board2 = selection2 ? findBoard(rate2Boards, selection2.boardCode) : null;
  const board3 = selection3 ? findBoard(rate3Boards, selection3.boardCode) : null;
  const board4 = selection4 ? findBoard(rate4Boards, selection4.boardCode) : null;

  const firstAvailableBoard = board1 || board2 || board3 || board4;

  const totalAmounts = {};
  const totalAmountsBefore = {};

  if (firstAvailableBoard && firstAvailableBoard.amount) {
    Object.keys(firstAvailableBoard.amount).forEach((currencyCode) => {
      const amounts1 = board1?.amount?.[currencyCode] || null;
      const amounts2 = board2?.amount?.[currencyCode] || null;
      const amounts3 = board3?.amount?.[currencyCode] || null;
      const amounts4 = board4?.amount?.[currencyCode] || null;

      const totalAmounts1 = amounts1 ? amounts1.amount : 0;
      const totalAmounts2 = amounts2 ? amounts2.amount : 0;
      const totalAmounts3 = amounts3 ? amounts3.amount : 0;
      const totalAmounts4 = amounts4 ? amounts4.amount : 0;

      totalAmounts[currencyCode] = totalAmounts1 + totalAmounts2 + totalAmounts3 + totalAmounts4;

      const totalAmountsBefore1 = amounts1 ? amounts1.amount_before : 0;
      const totalAmountsBefore2 = amounts2 ? amounts2.amount_before : 0;
      const totalAmountsBefore3 = amounts3 ? amounts3.amount_before : 0;
      const totalAmountsBefore4 = amounts4 ? amounts4.amount_before : 0;

      totalAmountsBefore[currencyCode] = totalAmountsBefore1 + totalAmountsBefore2
      + totalAmountsBefore3 + totalAmountsBefore4;
    });
  }

  return {
    totalAmounts,
    totalAmountsBefore,
    boardPrices: getBoardsAmounts(selection1, selection2, selection3, selection4),
    availId: board1?.avail_id || null,
  };
};

const updateCustomRoomsEvents = (state) => {
  const selection = state.booking.multipleRoomSelection;
  const { utmParams } = state.bookingFormState;
  const roomsSelection = [];
  if (selection[MULTIPLE_ROOMS.FIRST]) {
    roomsSelection.push(selection[MULTIPLE_ROOMS.FIRST]);
  }
  if (selection[MULTIPLE_ROOMS.SECOND]) {
    roomsSelection.push(selection[MULTIPLE_ROOMS.SECOND]);
  }
  if (selection[MULTIPLE_ROOMS.THIRD]) {
    roomsSelection.push(selection[MULTIPLE_ROOMS.THIRD]);
  }
  if (selection[MULTIPLE_ROOMS.FOURTH]) {
    roomsSelection.push(selection[MULTIPLE_ROOMS.FOURTH]);
  }
  return roomsSelection.map((roomSelection) => (
    chooseRoomEvent({
      room: roomSelection.room,
      rateCode: roomSelection.rateCode,
      boardCode: roomSelection.boardCode,
      hotel: state.booking.hotel,
      userCurrency: state.booking.filterCurrency,
      lastRequestedInfo: state.booking.last_requested_info,
      lang: state.i18nState.lang,
      utmSource: utmParams ? utmParams.utm_source : undefined,
    })
  ));
};

/* Sets the combination of Room / Rate / Board that the user has made for an availability */
export const updateCustomRoomsSelection = ({ boardCode, rateCode }) => (dispatch, getState) => {
  const {
    FIRST, SECOND, THIRD, FOURTH,
  } = MULTIPLE_ROOMS;

  const state = getState();
  const stateSelection = state.booking.multipleRoomSelection;
  const newSelection = { ...stateSelection }; // we want to handle changes in a clone
  if (boardCode) {
    if (newSelection[FIRST]) newSelection[FIRST].boardCode = boardCode;
    if (newSelection[SECOND]) newSelection[SECOND].boardCode = boardCode;
    if (newSelection[THIRD]) newSelection[THIRD].boardCode = boardCode;
    if (newSelection[FOURTH]) newSelection[FOURTH].boardCode = boardCode;
  }
  if (rateCode) {
    if (newSelection[FIRST]) newSelection[FIRST].rateCode = rateCode;
    if (newSelection[SECOND]) newSelection[SECOND].rateCode = rateCode;
    if (newSelection[THIRD]) newSelection[THIRD].rateCode = rateCode;
    if (newSelection[FOURTH]) newSelection[FOURTH].rateCode = rateCode;
  }
  const amounts = addAmounts(newSelection[FIRST], newSelection[SECOND], newSelection[THIRD], newSelection[FOURTH]);
  newSelection.totalAmounts = amounts.totalAmounts;
  newSelection.totalAmountsBefore = amounts.totalAmountsBefore;
  newSelection.boardPrices = amounts.boardPrices;

  // We register the GA event for RoomChoose
  const roomEvents = updateCustomRoomsEvents(state);
  dispatch(sendGTMEvents(roomEvents));

  dispatch({
    type: 'BOOKING_SET_MULTIPLE_ROOM_SELECTION', newSelection,
  });
};

/* Sets the combination of Room / Rate / Board that the user has made for an availability */
export const setMultipleRoomSelection = (roomSelection) => (dispatch, getState) => {
  const {
    FIRST, SECOND, THIRD, FOURTH,
  } = MULTIPLE_ROOMS;
  const newSelection = {
    isComplete: false,
    [FIRST]: null,
    [SECOND]: null,
    [THIRD]: null,
    [FOURTH]: null,
  };
  if (roomSelection !== null) {
    const currentSelection = getState().booking.multipleRoomSelection;
    newSelection[FIRST] = roomSelection.roomId === FIRST ? roomSelection : currentSelection[FIRST];
    newSelection[SECOND] = roomSelection.roomId === SECOND ? roomSelection : currentSelection[SECOND];
    newSelection[THIRD] = roomSelection.roomId === THIRD ? roomSelection : currentSelection[THIRD];
    newSelection[FOURTH] = roomSelection.roomId === FOURTH ? roomSelection : currentSelection[FOURTH];

    const amounts = addAmounts(newSelection[FIRST], newSelection[SECOND], newSelection[THIRD], newSelection[FOURTH]);
    newSelection.isComplete = true;
    newSelection.totalAmounts = amounts.totalAmounts;
    newSelection.totalAmountsBefore = amounts.totalAmountsBefore;
    newSelection.availId = amounts.availId;
    newSelection.boardPrices = amounts.boardPrices;
    const firstKey = newSelection[FIRST] && newSelection[FIRST].room ? newSelection[FIRST].room.code : '';
    const secondKey = newSelection[SECOND] && newSelection[SECOND].room ? newSelection[SECOND].room.code : '';
    const thirdKey = newSelection[THIRD] && newSelection[THIRD].room ? newSelection[THIRD].room.code : '';
    const fourthKey = newSelection[FOURTH] && newSelection[FOURTH].room ? newSelection[FOURTH].room.code : '';
    newSelection.roomsKey = `${firstKey}_${secondKey}_${thirdKey}_${fourthKey}`;
  }
  dispatch({
    type: 'BOOKING_SET_MULTIPLE_ROOM_SELECTION', newSelection,
  });
};

export const setNullRoomSelection = (roomIndex) => (dispatch, getState) => {
  // Este método lo ocupamos cuando le damos click en editar cuarto, lo remueve de props.multipleRoomSelection, es decir, lo
  // setea en null para que visualmente se vea que se debe seleccionar
  const {
    FIRST, SECOND, THIRD, FOURTH,
  } = MULTIPLE_ROOMS;
  const newSelection = {
    isComplete: false,
    [FIRST]: null,
    [SECOND]: null,
    [THIRD]: null,
    [FOURTH]: null,
  };

  const currentSelection = getState().booking.multipleRoomSelection;
  newSelection[FIRST] = roomIndex === FIRST ? null : currentSelection[FIRST];
  newSelection[SECOND] = roomIndex === SECOND ? null : currentSelection[SECOND];
  newSelection[THIRD] = roomIndex === THIRD ? null : currentSelection[THIRD];
  newSelection[FOURTH] = roomIndex === FOURTH ? null : currentSelection[FOURTH];

  const amounts = addAmounts(newSelection[FIRST], newSelection[SECOND], newSelection[THIRD], newSelection[FOURTH]);
  newSelection.isComplete = true;
  newSelection.totalAmounts = amounts.totalAmounts;
  newSelection.totalAmountsBefore = amounts.totalAmountsBefore;
  newSelection.availId = amounts.availId;
  newSelection.boardPrices = amounts.boardPrices;
  const firstKey = newSelection[FIRST] && newSelection[FIRST].room ? newSelection[FIRST].room.code : '';
  const secondKey = newSelection[SECOND] && newSelection[SECOND].room ? newSelection[SECOND].room.code : '';
  const thirdKey = newSelection[THIRD] && newSelection[THIRD].room ? newSelection[THIRD].room.code : '';
  const fourthKey = newSelection[FOURTH] && newSelection[FOURTH].room ? newSelection[FOURTH].room.code : '';
  newSelection.roomsKey = `${firstKey}_${secondKey}_${thirdKey}_${fourthKey}`;

  dispatch({
    type: 'BOOKING_SET_MULTIPLE_ROOM_SELECTION', newSelection,
  });
};

/* Sets the hotel nearby the searched one when there was no availability */
const setNearbyHotels = (nearbyHotels) => ({
  type: 'BOOKING_SET_NEARBY_HOTELS', nearbyHotels,
});

/* Sets the hotel nearby the searched one when there was no availability */
const setZoneAvailHotels = (zoneAvailHotels) => ({
  type: 'BOOKING_SET_ZONE_AVAIL_HOTELS', zoneAvailHotels,
});

/* Sets whether or not an availability search is in progress, eg. to show a waiting screen */
const setLoading = (loading) => ({
  type: 'BOOKING_SET_AVAIL_LOADING', loading,
});

/** ************************************************************************** */
/* Inyecta el código de Google Maps, excepto en desarrollo
/**************************************************************************** */

export const injectGoogleMaps = () => (dispatch) => {
  if (window.google && window.google.maps) {
    return;
  }
  dispatch({ type: 'BOOKING_INJECT_GMAPS' });
  const x = document.getElementsByTagName('script')[0];
  if (x) {
    const scriptSrc = `maps.google.com/maps/api/js?key=${process.env.GOOGLE_MAPS_KEY}`;
    const sc = document.createElement('script');
    sc.type = 'text/javascript';
    sc.async = true;
    sc.src = `${document.location.protocol === 'https:' ? 'https://' : 'http://'}${scriptSrc}`;
    sc.onerror = (e) => Raven.captureException(e);

    x.parentNode.insertBefore(sc, x);
  }
};

export const sendGTMEvents = (events, errorIfNot) => (dispatch, getState) => {
  events.forEach((ev) => {
    // We only want to set the "callcenter_mode" property in the cases where the event does not have the field
    if (!Object.prototype.hasOwnProperty.call(ev, 'callcenter_mode')) {
      const sessionState = getState().session;
      ev.callcenter_mode = isCallCenterUser(sessionState) || undefined; // eslint-disable-line no-param-reassign
    }
    addEventToDataLayer(ev, errorIfNot);
  });
};

/** ************************************************************************** */
/* Modifica la divisa seleccionada por el usuario
/**************************************************************************** */
export const setUserCurrency = (currencyObj) => (dispatch, getState) => {
  const state = getState();
  const amounts = state.cart.amount || {};
  const userPrice = amounts[currencyObj.code] || 0;
  const gtmEvent = {
    event: 'currencyChange',
    userCurrencyCode: currencyObj.code,
    userPrice: userPrice.toFixed(2),
  };
  dispatch(sendGTMEvents([gtmEvent]));
  dispatch(setFilterCurrency(currencyObj));
};

export const increaseStayLength = (days, navigate) => (dispatch, getState) => {
  dispatch({ type: 'BOOKING_ADD_BOOKINGFORM_DAYS' });

  const state = getState().bookingFormState;
  const checkIn = moment(state.checkIn);
  const checkOut = checkIn.clone().add({ days });

  dispatch(setCheckIn(checkIn.format('YYYY-MM-DD')));
  dispatch(setCheckOut(checkOut.format('YYYY-MM-DD')));
  dispatch(refreshNights(checkIn, checkOut));
  dispatch(searchAvailability(navigate));
};

export const buildAvailUrl = () => (dispatch, getState) => {
  const state = getState();
  const { lang } = state.i18nState;
  const formState = state.bookingFormState;
  const distribution = distributionToParam(formState.distributions);
  const promoCode = formState.promoCode ? `${formState.promoCode}/` : '';
  const baseUrl = process.env[`${formState.chain}_BOOKING`];
  const stateForAgentCode = store.getState();
  const agentCodeEnteredInBookingForm = stateForAgentCode.booking.agentCode ? stateForAgentCode.booking.agentCode : '-';
  const agentSellEnteredInBookingForm = stateForAgentCode.booking.agentSell
    ? stateForAgentCode.booking.agentSell : 'false';
  const rateType = stateForAgentCode.bookingFormState.rateType
    ? stateForAgentCode.bookingFormState.rateType : 'PUBLIC_RATE';
  const agentUseCoins = stateForAgentCode.bookingFormState.agentUseCoins
    ? stateForAgentCode.bookingFormState.agentUseCoins : 'true';
  const path = `v3/search/${lang}/${formState.origin}/share-url/`
          + `${formState.checkIn}/${formState.checkOut}/hotel/${formState.destination.pk}/${distribution}/
          ${agentCodeEnteredInBookingForm}/${rateType}/${agentSellEnteredInBookingForm}/${agentUseCoins}/${promoCode}`;
  return `${baseUrl}/${path}`;
};

export const updateRatesForNewUse = (roomCode, newUse) => (dispatch, getState) => {
  const state = getState();
  const formState = state.bookingFormState;
  const { hotel } = state.booking;

  const requestUse = toUseWithAges(hotel, newUse);
  const { ages } = requestUse;
  const urlParts = [
    process.env[`${formState.chain}_CHAIN`],
    formState.sessionId,
    formState.destination.code, // this is the dingus code
    roomCode, // this is the dingus code
    formState.checkIn,
    formState.checkOut,
    requestUse.adults,
    requestUse.childs,
    ages.length === 0 ? '-' : ages.join('@'),
  ];
  if (formState.promoCode) {
    urlParts.push(formState.promoCode);
  }

  const url = `api-pb/room-rates/${urlParts.join('/')}/`;
  return apiGet(url).then((response) => {
    if (response.rates.length === 0) {
      return false;
    }

    // We are going to update the rates of the room that has changed.
    const newRooms = hotel.rooms[FREE_MODE_ID].map((room) => {
      if (room.code === roomCode) {
        return {
          ...room,
          rates: response.rates,
          use_requested: newUse,
        };
      }
      return room;
    });

    const updatedHotel = { ...hotel, rooms: { [FREE_MODE_ID]: newRooms } };
    dispatch(setHotel(updatedHotel));
    return true;
  }).catch(() => false);
};

const completeAvailableHotels = (hotels, requestType) => hotels.map((hotel) => {
  const updatedHotel = { ...hotel };

  // IMPORTANT we unify the data structure from FREE_MODE by setting the result to be a Map always
  if (requestType.isFreeModeSelected) {
    updatedHotel.rooms = { [FREE_MODE_ID]: updatedHotel.rooms };
  }

  // For each room group, we set which is the "best value" room, as the one with the lower price
  const roomIds = Object.keys(updatedHotel.rooms);
  roomIds.forEach((roomId) => {
    const currentRooms = updatedHotel.rooms[roomId];
    if (currentRooms.length > 0) {
      currentRooms.sort(sortByPriceFrom);
      currentRooms[0].best_rate = true;
    }
  });
  return updatedHotel;
});

const getMinStay = (not_availables) => {
  let minStay = 0;
  not_availables.forEach((min) => {
    if (parseInt(min.min_stays, 10) > minStay) {
      minStay = parseInt(min.min_stays, 10);
    }
  });
  return minStay;
};

const hasNotStay = (not_availables) => {
  let notStay = false;
  not_availables.forEach((not_avail) => {
    if (not_avail.reason_not_aviable === 'NotStay') {
      notStay = true;
    }
  });
  return notStay;
};

const getCurrentCurrency = (response, filterCurrency) => {
  if (filterCurrency) {
    // Get the actual currency in case the user has already done a search and changed the currency.
    return response.currencies.find((currency) => currency.code === filterCurrency.code);
  }
  // Customer's currency listed by priority:
  // 1. The default hotel's currency (default_currency)
  // 2. The first currency supported by the hotel.
  const specialCurrencyHotel = response.availables.find((hotel) => hotel.default_currency);
  return response.currencies.find(
    (currency) => specialCurrencyHotel === undefined || currency.code === specialCurrencyHotel.default_currency,
  );
};

const setAvailabilityResponse = (response, state, navigate, dispatch) => {
  const {
    availables, not_availables, requested_info, currencies, error,
  } = response;
  const isCallCenter = isCallCenterUser(state.session);
  const defCurrencyCode = (
    requested_info.custom_currency_code && isCallCenter === false ? requested_info.custom_currency_code : 'USD'
  );
  // Antes se seteaba USD como default currency, pero ahora se setea el currency tomando en cuenta
  // primero si hay un custom_currency_code como en los hoteles de México
  // Para que funcione bien con el heat map, que muestra precio de las habitaciones en una currency que depende
  // de la ip, entonces si me muestra los precios en mxn, lo ideal es que al mostrar las disponibilidades las muestre en mxn
  const defCurrency = { code: defCurrencyCode }; // Added because in THOA somehow we dont have a value in state.booking.filterCurrency
  const currency = getCurrentCurrency(response,
    state.booking.filterCurrency ? state.booking.filterCurrency : defCurrency);
  // Basic response details
  dispatch(setCurrencies(currencies));
  dispatch(setFilterCurrency(currency));
  dispatch(setLastRequestedInfo(requested_info));

  // Availability results
  const { isFreeModeSelected, destType, destination } = state.bookingFormState;
  const requestType = { isFreeModeSelected, isCallCenter };
  const availableHotels = completeAvailableHotels(availables, requestType);

  dispatch({ type: 'HOTEL_GET_LONGSTAYS', banner: null });

  // Checking where to go next depending on the availability results
  const availHotel = availableHotels.length > 0 ? availableHotels[0] : not_availables[0];
  if (!availHotel) {
    // No results for either available or non available hotels
    navigate('/error/', { state: { showForm: true } });
  } else if (availHotel.available && destType !== 'zone') {
    dispatch(setAvailError(error));
    // The hotel that we searched for has valid results, so we jump to /rooms
    const utmParams = state.bookingFormState.utmParams || {};
    const event = roomListLoadedEvent({
      lang: state.i18nState.lang,
      hotel: availHotel,
      lastRequestedInfo: requested_info,
      userCurrency: currency,
      utmSource: utmParams.utmSource,
    });
    dispatch(sendGTMEvents([event]));
    dispatch(loadLongStayBanner(availHotel.code, state.bookingFormState.checkIn, state.bookingFormState.checkOut));
    dispatch(setAvailabilityHotel(availHotel));
    // Now we move to the availability response page
    navigate(state.bookingFormState.isFreeModeSelected ? '/all-rooms/' : '/rooms/', { replace: true });
  } else if (availHotel.available && destType === 'zone') {
    dispatch(setNearbyHotels(null));
    dispatch(setZoneAvailHotels(availableHotels));
    dispatch(setAvailabilityHotel(destination));
    navigate('/hotels/');
  } else {
    dispatch(setAvailError('NO_AVAIL'));
    if (not_availables.length > 0 && hasNotStay(not_availables)) {
      // The hotel that we searched for has a longer minimum stay
      dispatch(setAvailabilityHotel(destination));
      dispatch(setAvailabilityMinStay(parseInt(getMinStay(not_availables), 10)));
    } else {
      // Other hotels can be available, let's redirect to Nearby hotels
      // But if they are call center, the hotel is set so that they can omit restrictions if they want to
      if (isCallCenter) {
        dispatch(setAvailabilityHotel(availHotel));
      } else {
        dispatch(setHotel(null));
      }
      navigate('/hotels/');
    }
  }
};

const buildAvailAPIUrl = (formState, distris) => {
  const hasDistris = distris !== FREE_MODE_DISTRIBUTION;
  const urlParts = [
    hasDistris ? 'rooms' : '',
    process.env[`${formState.chain}_CHAIN`],
    formState.sessionId,
    formState.checkIn,
    formState.checkOut,
    formState.destType,
    formState.destination.pk,
    hasDistris ? distris : '',
    formState.promoCode,
  ];
  // We can just skip the non empty parts as there are no numbers which we might want to keep (eg. childs with 0 value)
  const subUrl = urlParts.filter((part) => !!part).join('/');
  return `api-pb/availability/hotel/${subUrl}/?origin=${formState.origin}`;
};

// Dispatches the availability action to the api and manage the response.
export const startSearch = (context, navigate) => (dispatch, getState) => {
  dispatch(setLoading(true));
  dispatch(setAvailabilityMinStay(0));
  dispatch(setAvailError('00'));

  const state = getState();
  const { lang } = state.i18nState;
  const formState = state.bookingFormState;
  const { loggedUser } = state.loyalty;

  const { isFreeModeSelected } = formState;
  const distris = isFreeModeSelected ? FREE_MODE_DISTRIBUTION : distributionToParam(formState.distributions);
  const availUrl = buildAvailAPIUrl(formState, distris);

  apiGet(availUrl, lang).then((response) => {
    dispatch(setLoading(false));
    if (response) {
      setAvailabilityResponse(response, state, navigate, dispatch);
      if (loggedUser !== null && loggedUser.email !== '') {
        dispatch(getMemberInfo({ email: loggedUser.email }));
      }
    } else {
      // If the response is null, it means that there was an error at the server level
      const errorMsg = context.t('An error has occurred, returning to home.');
      dispatch(setAlertMessage({
        message: errorMsg, level: 'error', returnIn: 5, returnTo: '/',
      }));
    }
  });
};

/** ************************************************************************** */
/* Acción cuando una reserva ha finalizado correctamente y se salta a la
/* pàgina de gracias
/**************************************************************************** */
const confirmOkAndGotoThanks = (locator, email, navigate) => (dispatch) => {
  dispatch({ type: 'BOOKING_CONFIRM_OK_GOTO_THANKS' });
  dispatch(setBookLocator(locator));
  dispatch(setBookEmail(email));
  // Cargamos la reserva para obtener editBooking al igual que en /my-booking/
  // De esta manera obtenemos información importante que se usa en el componente
  // bookingDetails para saber con qué correo cargar la booking, con el email
  // del cliente o con el email del agente para saber si mostrar la información
  // para el cliente o para el agente. Todo eso para el escenario de
  // reservas con agentCode y agentSell en true ya sea tarifa pública o neta
  const state = store.getState();
  const { lang } = state.i18nState;
  // Acá si es agente, en correo debe mandarse el correo del agente
  const { agentCode, agentSell } = state.booking;
  let emailToQueryBooking = email;
  if (agentCode != null && agentCode !== '' && agentSell != null && agentSell === 'true') {
    emailToQueryBooking = state.loyalty.memberInfo.email;
  }
  dispatch(getBookingDetail(
    lang,
    locator,
    'email',
    emailToQueryBooking,
  )).then((r) => {
    if (r && r.booking) {
      dispatch(setEditBooking(r.booking));
      navigate('/thanks/');
      navigate(0);
      dispatch(cleanConfirmationForm());
    }
    // Cargamos la reserva para obtener editBooking al igual que en /my-booking/
  });
};

/** ************************************************************************** */
/* Acción cuando se confirma una reserva correctamente y si tiene que saltar
/* al TPV
/**************************************************************************** */

const goToTpv = (tpvForm) => {
  if (tpvForm && tpvForm.substr(0, 4) === 'http') {
    // The payment process is performed through a simple redirect
    window.location = tpvForm;
  } else {
    // The payment process is performed through an injected form
    jQuery('body').append(tpvForm);
    jQuery('#tpv_form,#pago3d').submit();
  }
};

const confirmedBookingAndGoToTPV = (tpvForm, locator, email) => (dispatch) => {
  dispatch({ type: 'BOOKING_CREATED_OK_GOTO_TPV' });
  dispatch(setBookLocator(locator));
  dispatch(setBookEmail(email));
  dispatch(cleanConfirmationForm());

  window.history.pushState({}, '', '/thanks/');

  goToTpv(tpvForm);
};

const sendBookingCreatedEvent = (state, confData, locator, lang, isPending) => (dispatch) => {
  try {
    const newBookingEvent = bookingCreatedEvent(state, confData, locator, lang, isPending);
    dispatch(sendGTMEvents([newBookingEvent], `Confirmation Event: ${locator}`));
  } catch (e) {
    Raven.captureException(e);
  }
};

export const initializeSignalR = (navigate, location, signalr_server, sessionId, locator) => (dispatch) => {
  if (sessionId != null) {
    const hubConnection = new signalR.HubConnectionBuilder()
      .withUrl(`${signalr_server}/SignalRHub`)
      .build();

    let connectionId = sessionId;
    if (locator != null) {
      connectionId = locator;
    }

    hubConnection.start()
      .then(() => hubConnection.invoke('Connect', connectionId));
    // eslint-disable-next-line no-unused-vars
    hubConnection.on('ReceiveMessage', (user, message) => {
      //const encodedMsg = `${user} says ${message}`;
      // let li = document.createElement("li");
      // li.textContent = encodedMsg;
      // document.getElementById("messagesList").appendChild(li);
      // console.log('encodedMsg', encodedMsg);
      if (message === 'AdyenPaymentProcessedSuccess' || message === 'AdyenPaymentProcessedError') {
        // Acá si es agente, en correo debe mandarse el correo del agente
        const state = store.getState();
        const { lang } = state.i18nState;
        const {
          agentCode,
          agentSell,
          book_locator,
          book_email,
        } = state.booking;
        let emailToQueryBooking = book_email;
        if (agentCode != null && agentCode !== '' && agentSell != null && agentSell === 'true') {
          emailToQueryBooking = state.loyalty.memberInfo.email;
        }
        dispatch(getBookingDetail(
          lang,
          book_locator,
          'email',
          emailToQueryBooking,
        )).then((r) => {
          if (r && r.booking) {
            dispatch(setEditBooking(r.booking));
            if (location.pathname !== '/thanks/') {
              navigate('/thanks/');
            }
            navigate(0);
          }
          // Cargamos la reserva para obtener editBooking al igual que en /my-booking/
        });
      }
    });
  }
};
// eslint-disable-next-line no-promise-executor-return
//const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export const bookingSaveContactInfo = (context, formData, navigate, locator) => (dispatch, getState) => {
  // Used by Call Center to generate a customer to pay for Refundable bookings
  const state = getState();
  const {
    origin, sessionId, utmParams,
  } = state.bookingFormState;
  const { lang } = state.i18nState;

  // Enviamos los datos del cliente y la configuración adicional de la reserva
  const confData = {
    ...formData,
    lang,
    origin,
    locator,
    // This is to avoid book close errors for customers using old versions of the page
    // Then on the API we need to remove them and make "extra_params" mandatory instead
    extra_params: buildExtraAnalyticsParams(utmParams),
    device: getDeviceName(state.booking.device),
  };

  return apiPost(`api-pb/save-contact-info/${sessionId}/`, confData, lang).then((response) => response);
};

// eslint-disable-next-line no-unused-vars
export const CheckCardHasPreviousFailedAdyenPaymentAttempts = (locator, card_fingerprint, lang) => (dispatch) =>
  apiPost('api-pb/check-card-has-previous-failed-adyen-attempts/',
    { locator, card_fingerprint },
    lang).then((paymentResp) => (
    { show_warning: paymentResp.show_warning })).catch(() => ({ show_warning: false }));

/** ************************************************************************** */
/* Acción de confirmar reserva
/**************************************************************************** */
export const bookingConfirm = (context, formData, navigate,
  book_locator = null, book_email = null) => (dispatch, getState) => {
  dispatch({ type: 'BOOKING_CONFIRM' });
  dispatch(setBookLocator(book_locator));
  if (book_locator == null) {
    dispatch(setBookEmail(null));
  } else {
    dispatch(setBookEmail(book_email));
  }

  const state = getState();
  const {
    origin, sessionId, utmParams,
  } = state.bookingFormState;
  const { lang } = state.i18nState;

  // Enviamos los datos del cliente y la configuración adicional de la reserva
  const confData = {
    locator: book_locator,
    ...formData,
    lang,
    origin,
    // This is to avoid book close errors for customers using old versions of the page
    // Then on the API we need to remove them and make "extra_params" mandatory instead
    extra_params: buildExtraAnalyticsParams(utmParams),
    device: getDeviceName(state.booking.device),
    previous_optin_accepted: state.loyalty.optinAccepted,
  };

  const onError = (response) => {
    response.json().then((error) => {
      dispatch(setConfirmErrorState(error[0]));
    });
  };

  let url = `api-pb/confirmation/${sessionId}/`;
  if (formData.payment_method.code === 'ADYEN') {
    url = `api-pb/confirmationAdyen/${sessionId}/`;
  }
  return apiPost(url, confData, lang, onError).then((response) => {
    if (!response) {
      // There was an error and the booking was not created
      return 'ERR';
    }

    dispatch(emptyShoppingCart());

    // Send the new booking created event
    dispatch(sendBookingCreatedEvent(state, confData, response.locator, lang, Boolean(response.tpv_form)));

    if (response.tpv_form && !confData.send_payment_email) {
      if (response.tpv_form === 'ALREADY_PAID') {
        dispatch(confirmOkAndGotoThanks(response.locator, response.email, context.router));
        return 'OK';
      }
      // We do not need to redirect to metapasarela because with adyen the payment is done in the widget
      if (formData.payment_method.code === 'ADYEN') { // If method is Adyen
        if (response.tpv_form.amount === 0) {
          dispatch(confirmOkAndGotoThanks(response.locator, response.email, context.router));
          return 'OK';
        }
        const createAdyenCheckout = async (id, sessionData, locator, email) => {
          const translations = {
            'es-ES': {
              installments: 'Meses sin intereses',
            },
            'en-US': {
              installments: 'Interest free installments',
            },
          };
          const installmentOptionsList = response.tpv_form.installments_options.split(',').map(Number);
          let adyen_lang = 'en-US';
          switch (lang) {
            case 'es':
              adyen_lang = 'es-ES';
              break;
            case 'en':
              adyen_lang = 'en-US';
              break;
            case 'de':
              adyen_lang = 'de-DE';
              break;
            case 'fr':
              adyen_lang = 'fr-FR';
              break;
            default:
              adyen_lang = 'en-US';
          }

          const hasCreditInPaymentMethods = (onBinLookupResponse) => {
            if (!onBinLookupResponse?.paymentMethodVariants) {
              return false;
            }
            const variants = onBinLookupResponse.paymentMethodVariants.map((v) => v.toLowerCase());
            // Caso 1: Tiene la palabra "credit" explícitamente
            if (variants.some((variant) => variant.includes('credit'))) {
              return true;
            }
            // Caso 2: Para AMEX en México, asumimos que son de crédito
            if (onBinLookupResponse.issuingCountryCode === 'MX'
                && variants.some((variant) => variant.includes('amex'))) {
              return true;
            }
            return false;
          };

          const showInstallments = () => {
            const el = document.querySelector('.adyen-checkout__installments');
            if (el) {
              el.style.display = 'block';
            }
          };

          const hideInstallments = () => {
            const el = document.querySelector('.adyen-checkout__installments');
            if (el) {
              el.style.display = 'none';
            }
            // Resetear a la primera opción del dropdown de installments
            const dropdownButton = document.querySelector('.adyen-checkout__dropdown__button');
            const dropdownList = document.querySelector('.adyen-checkout__dropdown__list');
            if (dropdownButton && dropdownList) {
              const firstOption = dropdownList.querySelector('li');
              if (firstOption) {
                // Simula el click para seleccionar la primera opción (1 cuota)
                firstOption.click();
              }
            }
          };

          const showCardWarning = () => {
            let warningEl = document.querySelector('.adyen-card-warning');
            if (!warningEl) {
              warningEl = document.createElement('div');
              warningEl.className = 'adyen-card-warning';
              warningEl.style.cssText = 'display:flex;align-items:flex-start;justify-content:space-between;'
                + 'gap:8px;background-color:#fff8e6;color:#6b4a00;border:1px solid #f5c451;border-radius:4px;'
                + 'padding:8px 10px 8px 12px;margin-top:-7px;margin-bottom:4px;font-size:13px;line-height:1.4;';

              const textEl = document.createElement('span');
              textEl.className = 'adyen-card-warning__text';
              textEl.textContent = context.t('Multiple payment attempts were detected with this card.'
                + ' Please verify that your details are correct before continuing. Check your banking app'
                + ' to confirm your available balance or see if you received a security alert.');

              const closeBtn = document.createElement('button');
              closeBtn.type = 'button';
              closeBtn.className = 'adyen-card-warning__close';
              closeBtn.setAttribute('aria-label', context.t('Close'));
              closeBtn.textContent = '\u00D7';
              closeBtn.style.cssText = 'background:transparent;border:none;cursor:pointer;color:#6b4a00;'
                + 'font-size:18px;line-height:1;padding:0;margin:0;flex-shrink:0;opacity:0.7;';
              closeBtn.onmouseenter = () => { closeBtn.style.opacity = '1'; };
              closeBtn.onmouseleave = () => { closeBtn.style.opacity = '0.7'; };
              closeBtn.addEventListener('click', hideCardWarning);

              warningEl.appendChild(textEl);
              warningEl.appendChild(closeBtn);

              // Buscamos el campo dentro del dropin actualmente montado (evita instancias
              // huérfanas si hubo un remount) y lo insertamos justo debajo del Card number
              const dropinContainer = document.querySelector('#dropin-container');
              const cardNumberField = dropinContainer
                ? dropinContainer.querySelector('.adyen-checkout__field--cardNumber')
                : null;
              if (cardNumberField) {
                cardNumberField.insertAdjacentElement('afterend', warningEl);
              } else if (dropinContainer && dropinContainer.parentNode) {
                // Fallback por si Adyen cambia el nombre de la clase o aún no ha
                // terminado de renderizar el campo
                dropinContainer.parentNode.insertBefore(warningEl, dropinContainer);
              }
            }
            warningEl.style.display = 'flex';
          };

          const hideCardWarning = () => {
            const warningEl = document.querySelector('.adyen-card-warning');
            if (warningEl) {
              warningEl.style.display = 'none';
            }
          };

          const handleBinLookup = (binData) => {
            const countryCode = binData?.issuingCountryCode;
            if (countryCode === 'MX' && hasCreditInPaymentMethods(binData)) {
              showInstallments();
            } else {
              hideInstallments();
            }
          };

          const handleOnFieldValid = (data) => {
            if (data.endDigits && data.issuerBin) {
              const cardFingerprint = `${data.issuerBin}-${data.endDigits}`;
              dispatch(CheckCardHasPreviousFailedAdyenPaymentAttempts(response.locator, cardFingerprint, lang))
                .then(({ show_warning }) => {
                  if (show_warning) {
                    showCardWarning();
                  } else {
                    hideCardWarning();
                  }
                });
            }
          };

          // Configuración base
          const checkoutConfig = {
            environment: response.tpv_form.test_mode === true ? 'test' : 'live',
            locale: adyen_lang,
            translations,
            clientKey: response.tpv_form.adyen_client_key,
            analytics: {
              enabled: true,
            },
            session: {
              id,
              sessionData,
            },
            showPayButton: true,
            // eslint-disable-next-line no-unused-vars
            onPaymentCompleted: async (result, component) => {
              dispatch(confirmOkAndGotoThanks(locator, email, navigate));
            },
            // eslint-disable-next-line no-unused-vars
            onError: (error, component) => {
              // Manejo de error opcional
            },
          };

          const adyenCheckout = await AdyenCheckout(checkoutConfig);
          // Configuración base de Dropin
          const dropinConfig = {
            paymentMethodsConfiguration: {
              card: {
                showBrandIcon: true,
                hasHolderName: true,
                holderNameRequired: true,
                billingAddressRequired: response.tpv_form.billing_address_required,
                placeholders: {
                  cardNumber: '1234 5678 9012 3456',
                  expiryDate: 'MM/YY',
                  securityCodeThreeDigits: '123',
                  securityCodeFourDigits: '1234',
                  holderName: 'J. Smith',
                },
                onBinLookup: handleBinLookup,
                onFieldValid: handleOnFieldValid,
              },
            },
            instantPaymentTypes: ['applepay', 'googlepay'],
          };

          // Agregar installmentOptions solo si this.adyenOfferInstallments es true
          if (response.tpv_form.offer_installments) {
            dropinConfig.paymentMethodsConfiguration.installmentOptions = {
              card: {
                values: installmentOptionsList,
                //preselectedValue: 3 //Es opcional
                //supportedBrands: ['visa', 'mc'] //No es muy necesario ya que las tarjetas admitidas se manejan desde el portal
              },
            };
          }
          // eslint-disable-next-line no-unused-vars
          const adyenDropin = new Dropin(adyenCheckout, dropinConfig).mount('#dropin-container');
          setTimeout(() => {
            hideInstallments();
          }, 1000);
        };
        createAdyenCheckout(response.tpv_form.id, response.tpv_form.sessionData, response.locator, response.email);
        //Para que funcione cuando le den click en cancel debemos setear el book locator y el email
        dispatch(setBookLocator(response.locator));
        dispatch(setBookEmail(response.email));
      } else { // If method is Metapasarela
        // The payment is done via TPV. We'll need to redirect to the Payment system page
        dispatch(confirmedBookingAndGoToTPV(response.tpv_form, response.locator, response.email));
      }
    } else {
      // The response is valid, and the payment is done at the hotel, so we'll redirect to the Thanks page
      dispatch(confirmOkAndGotoThanks(response.locator, response.email, navigate));
    }

    return 'OK';
  });
};

/** ************************************************************************** */
/* Acción de pagar desde la página de gracias con adyen
/**************************************************************************** */
export const bookingAdyenPay = (context, locator, email,
  adyen_tpv_method, navigate) => (dispatch, getState) => {
  // adyen_tpv_method can be tpv-form-adyen or tpv-upfront-adyen-payment-form
  dispatch({ type: 'BOOKING_CONFIRM' });
  //dispatch(setBookLocator(null));
  //dispatch(setBookEmail(null));
  const state = getState();
  const { lang } = state.i18nState;
  const onError = (response) => {
    response.json().then((error) => {
      dispatch(setConfirmErrorState(error[0]));
    });
  };
  const url = `api-pb/booking/${adyen_tpv_method}/`;
  const confData = {
    locator,
    email,
    method: 'ADYEN',
    card: 'VISA', // It can be whatever, it is not used, but we use the same
    // serializer as other payment methods like metapasarela
    // What changes is the serializer from data that is returned
  };
  return apiPost(url, confData, lang, onError).then((response) => {
    if (!response) {
      // There was an error and the booking was not created
      return 'OK';
      //return 'ERR';
    }

    //dispatch(emptyShoppingCart()); // Ya no haremos eso por la parte de editar la booking
    // Send the new booking created event
    //dispatch(sendBookingCreatedEvent(state, confData, response.locator, lang, Boolean(response.tpv_form)));
    if (response.tpv_form) {
      if (response.tpv_form.amount === 0) {
        dispatch(confirmOkAndGotoThanks(locator, email, context.router));
        return 'OK';
      }
      // The payment is done via TPV. We'll need to redirect to the Payment system page
      // We do not need to redirect to metapasarela because with adyen the payment is done in the widget
      const createAdyenCheckout = async (id, sessionData) => {
        const translations = {
          'es-ES': {
            installments: 'Meses sin intereses',
          },
          'en-US': {
            installments: 'Interest free installments',
          },
        };
        const installmentOptionsList = response.tpv_form.installments_options.split(',').map(Number);
        let adyen_lang = 'en-US';
        switch (lang) {
          case 'es':
            adyen_lang = 'es-ES';
            break;
          case 'en':
            adyen_lang = 'en-US';
            break;
          case 'de':
            adyen_lang = 'de-DE';
            break;
          case 'fr':
            adyen_lang = 'fr-FR';
            break;
          default:
            adyen_lang = 'en-US';
        }
        const hasCreditInPaymentMethods = (onBinLookupResponse) => {
          if (!onBinLookupResponse?.paymentMethodVariants) {
            return false;
          }
          const variants = onBinLookupResponse.paymentMethodVariants.map((v) => v.toLowerCase());
          // Caso 1: Tiene la palabra "credit" explícitamente
          if (variants.some((variant) => variant.includes('credit'))) {
            return true;
          }
          // Caso 2: Para AMEX en México, asumimos que son de crédito
          if (onBinLookupResponse.issuingCountryCode === 'MX'
              && variants.some((variant) => variant.includes('amex'))) {
            return true;
          }
          return false;
        };

        const showInstallments = () => {
          const el = document.querySelector('.adyen-checkout__installments');
          if (el) {
            el.style.display = 'block';
          }
        };

        const hideInstallments = () => {
          const el = document.querySelector('.adyen-checkout__installments');
          if (el) {
            el.style.display = 'none';
          }
          // Resetear a la primera opción del dropdown de installments
          const dropdownButton = document.querySelector('.adyen-checkout__dropdown__button');
          const dropdownList = document.querySelector('.adyen-checkout__dropdown__list');
          if (dropdownButton && dropdownList) {
            const firstOption = dropdownList.querySelector('li');
            if (firstOption) {
              // Simula el click para seleccionar la primera opción (1 cuota)
              firstOption.click();
            }
          }
        };

        const showCardWarning = () => {
          let warningEl = document.querySelector('.adyen-card-warning');
          if (!warningEl) {
            warningEl = document.createElement('div');
            warningEl.className = 'adyen-card-warning';
            warningEl.style.cssText = 'display:flex;align-items:flex-start;justify-content:space-between;'
              + 'gap:8px;background-color:#fff8e6;color:#6b4a00;border:1px solid #f5c451;border-radius:4px;'
              + 'padding:8px 10px 8px 12px;margin-top:-7px;margin-bottom:4px;font-size:13px;line-height:1.4;';

            const textEl = document.createElement('span');
            textEl.className = 'adyen-card-warning__text';
            textEl.textContent = context.t('Multiple payment attempts were detected with this card.'
                + ' Please verify that your details are correct before continuing. Check your banking app'
                + ' to confirm your available balance or see if you received a security alert.');

            const closeBtn = document.createElement('button');
            closeBtn.type = 'button';
            closeBtn.className = 'adyen-card-warning__close';
            closeBtn.setAttribute('aria-label', context.t('Close'));
            closeBtn.textContent = '\u00D7';
            closeBtn.style.cssText = 'background:transparent;border:none;cursor:pointer;color:#6b4a00;'
              + 'font-size:18px;line-height:1;padding:0;margin:0;flex-shrink:0;opacity:0.7;';
            closeBtn.onmouseenter = () => { closeBtn.style.opacity = '1'; };
            closeBtn.onmouseleave = () => { closeBtn.style.opacity = '0.7'; };
            closeBtn.addEventListener('click', hideCardWarning);

            warningEl.appendChild(textEl);
            warningEl.appendChild(closeBtn);

            // Buscamos el campo dentro del dropin actualmente montado (evita instancias
            // huérfanas si hubo un remount) y lo insertamos justo debajo del Card number
            const dropinContainer = document.querySelector('#dropin-container');
            const cardNumberField = dropinContainer
              ? dropinContainer.querySelector('.adyen-checkout__field--cardNumber')
              : null;
            if (cardNumberField) {
              cardNumberField.insertAdjacentElement('afterend', warningEl);
            } else if (dropinContainer && dropinContainer.parentNode) {
              // Fallback por si Adyen cambia el nombre de la clase o aún no ha
              // terminado de renderizar el campo
              dropinContainer.parentNode.insertBefore(warningEl, dropinContainer);
            }
          }
          warningEl.style.display = 'flex';
        };

        const hideCardWarning = () => {
          const warningEl = document.querySelector('.adyen-card-warning');
          if (warningEl) {
            warningEl.style.display = 'none';
          }
        };

        const handleBinLookup = (binData) => {
          const countryCode = binData?.issuingCountryCode;
          if (countryCode === 'MX' && hasCreditInPaymentMethods(binData)) {
            showInstallments();
          } else {
            hideInstallments();
          }
        };

        const handleOnFieldValid = (data) => {
          if (data.endDigits && data.issuerBin) {
            const cardFingerprint = `${data.issuerBin}-${data.endDigits}`;
            dispatch(CheckCardHasPreviousFailedAdyenPaymentAttempts(locator, cardFingerprint, lang))
              .then(({ show_warning }) => {
                if (show_warning) {
                  showCardWarning();
                } else {
                  hideCardWarning();
                }
              });
          }
        };

        // Configuración base
        const checkoutConfig = {
          environment: response.tpv_form.test_mode === true ? 'test' : 'live',
          locale: adyen_lang,
          translations,
          clientKey: response.tpv_form.adyen_client_key,
          analytics: {
            enabled: true,
          },
          session: {
            id,
            sessionData,
          },
          showPayButton: true,
          // eslint-disable-next-line no-unused-vars
          onPaymentCompleted: async (result, component) => {
            dispatch(confirmOkAndGotoThanks(locator, email, navigate));
          },
          // eslint-disable-next-line no-unused-vars
          onError: (error, component) => {
            // Manejo de error opcional
          },
        };

        const adyenCheckout = await AdyenCheckout(checkoutConfig);
        // Configuración base de Dropin
        const dropinConfig = {
          paymentMethodsConfiguration: {
            card: {
              showBrandIcon: true,
              hasHolderName: true,
              holderNameRequired: true,
              billingAddressRequired: response.tpv_form.billing_address_required,
              placeholders: {
                cardNumber: '1234 5678 9012 3456',
                expiryDate: 'MM/YY',
                securityCodeThreeDigits: '123',
                securityCodeFourDigits: '1234',
                holderName: 'J. Smith',
              },
              onBinLookup: handleBinLookup,
              onFieldValid: handleOnFieldValid,
            },
          },
          instantPaymentTypes: ['applepay', 'googlepay'],
        };

        // Agregar installmentOptions solo si this.adyenOfferInstallments es true
        if (response.tpv_form.offer_installments) {
          dropinConfig.paymentMethodsConfiguration.installmentOptions = {
            card: {
              values: installmentOptionsList,
              //preselectedValue: 3 //Es opcional
              //supportedBrands: ['visa', 'mc'] //No es muy necesario ya que las tarjetas admitidas se manejan desde el portal
            },
          };
        }
        // eslint-disable-next-line no-unused-vars
        const adyenDropin = new Dropin(adyenCheckout, dropinConfig).mount('#dropin-container');
        setTimeout(() => {
          hideInstallments();
        }, 1000);
      };
      createAdyenCheckout(response.tpv_form.id, response.tpv_form.sessionData);
    }
    return 'OK';
  });
};

export const sendPaymentUpfrontLink = ({
  locator, email, session, lang,
}) => () => {
  // Used by Call Center to generate a customer to pay for Refundable bookings
  const request = { locator, email, session };
  return apiPost('api-pb/booking/upfront-payment-create/', request, lang).then((response) => response.payment_url);
};

export const createPaymentUpfront = (token, lang) => (dispatch) =>
// Used by the Customer to pay upfront for his Refundable booking before arriving to the hotel
  apiPost('api-pb/booking/upfront-payment-validate/', { token }, lang).then((paymentResp) => {
    if (!paymentResp.locator) {
      return { hasError: true };
    }
    // We want to load the booking corresponding to the upfront payment
    const { locator, email } = paymentResp;
    return dispatch(getBookingDetail(lang, locator, 'email', email));
  }).catch(() => ({ hasError: true }));

/** ************************************************************************** */
/* Carga la lista de países
/**************************************************************************** */
export const getCountries = () => (dispatch) => {
  dispatch({ type: 'BOOKING_GET_COUNTRIES' });
  return apiGet('api-pb/countries/');
};

/** ******************************************************************************************* */
/* Extrae los datos de las habitaciones en función de la selección actual de tarifa / régimen
/********************************************************************************************* */
export const calculateDiscount = (amount, amountBefore) => {
  const totalDiscount = amountBefore - amount;
  if (totalDiscount === 0) {
    return 0;
  }
  return Number((Math.floor(100 * (totalDiscount / amountBefore))).toFixed(2)); // rounded down, no decimals
};

/** ************************************************************************** */
/* Acción para resetear todos los campos del formulario
/**************************************************************************** */
const cleanConfirmationForm = () => (dispatch) => {
  dispatch({ type: 'BOOKING_CLEAN_CONFIRMATION_FORM' });
  dispatch(setConfirmFormState(confirmFormState));
};

/** ************************************************************************** */
/* Setea el mensaje de alerta
/**************************************************************************** */
export const setAlertMessage = (messageObj) => (dispatch) => {
  let data = null;
  if (messageObj) {
    const {
      message, level, returnIn, returnTo,
    } = messageObj;
    data = {
      text: message,
      level: level || 'success',
      returnIn,
      returnTo,
    };
  }
  dispatch({ type: 'BOOKING_SET_ALERT_MESSAGE', alertInfo: data });
};

/** ************************************************************************** */
/* Recupera los detalles de una reserva
/**************************************************************************** */
export const getBookingDetail = (lang, locator, valueType, value) => (dispatch) => {
  dispatch({ type: 'BOOKING_GET_BOOKING_DETAIL' });
  if (value) {
    return apiGet(`api-pb/booking-detail/${locator}/${valueType}/${value}/`, lang);
  }
  return apiGet(`api-pb/booking-detail-by-locator/${locator}/`, lang);
};

/** ************************************************************************** */
/* Permite realizar la llamada al formulario de TPV
/**************************************************************************** */
export const requestTPVForm = (formMethod, locator, email, method, card) => (dispatch) => {
  dispatch({ type: 'BOOKING_SET_BOOKING_PAYMENT_METHOD' });
  return apiPost(`api-pb/booking/${formMethod}/`, { // tpv-form, tpv-upfront-payment-form
    locator,
    email,
    method,
    card,
  }).then((paymentResponse) => goToTpv(paymentResponse.tpv_form));
};

/* Returns the details of the hotel given its code */
export const findHotelDestination = (countries, hotelCode) => {
  let destination = null;
  countries.forEach((country) => {
    country.zones.forEach((zone) => {
      const temp = zone.hotels.find((h) => h.code === hotelCode);
      if (temp) {
        destination = temp;
      }
    });
  });
  return destination;
};

/* Returns the details of the hotel given its code */
export const findZoneDestination = (countries, zonePk) => {
  let destination = null;
  countries.forEach((country) => {
    const temp = country.zones.find((z) => z.pk === zonePk);
    if (temp) {
      destination = temp;
    }
  });
  return destination;
};

/* Updates the hotel and destination for the availability results */
export const setAvailabilityHotel = (hotel) => (dispatch, getState) => {
  const state = getState();
  const { countries } = state.bookingFormState;
  const hotelForm = findHotelDestination(countries, hotel.code);
  const zoneForm = findZoneDestination(countries, hotel.pk);
  if (hotelForm) {
    dispatch(setHotel(hotel));
    dispatch(setDestination(hotelForm));
  } else if (zoneForm) {
    dispatch(setHotel(null));
    dispatch(setDestination(zoneForm));
  }
};

/** ************************************************************************** */
/* Dado un hotel, devuelve los hoteles cercanos con disponibilidad
/**************************************************************************** */
export const getNearbyHotels = (hotelCode) => (dispatch, getState) => {
  const state = getState();

  dispatch(setNearbyHotels(null));
  dispatch(setZoneAvailHotels(null));
  dispatch({ type: 'BOOKING_GET_NEARBY_HOTELS' });

  const formState = state.bookingFormState;
  const distributions = distributionToParam(formState.distributions);
  const promoCode = formState.promoCode ? `/${formState.promoCode}/` : '';
  const url = `api-pb/nearby-hotels/rooms/${process.env[`${formState.chain}_CHAIN`]}/${formState.sessionId}/`
          + `${hotelCode}/${formState.checkIn}/${formState.checkOut}/${distributions}${promoCode}`;

  return apiGet(url).then((response) => {
    if (response && response.hotels) {
      const { isFreeModeSelected } = state.bookingFormState;
      const requestType = { isFreeModeSelected, isCallCenter: isCallCenterUser(state.session) };
      const hotels = completeAvailableHotels(response.hotels, requestType);
      dispatch(setNearbyHotels(hotels));
      return hotels.length > 0;
    }
    return false;
  });
};

/** ************************************************************************** */
/* Devuelve el calendario de estancias mínimas de un hotel (2 meses)
/**************************************************************************** */
export const getHotelMinStayCalendar = (hotelCode, initialMonth) => (dispatch, getState) => {
  const state = getState();
  const formState = state.bookingFormState;

  const chainKey = process.env[`${formState.chain}_CHAIN`];
  const urlParts = [
    chainKey,
    formState.sessionId,
    hotelCode,
    initialMonth,
    moment(initialMonth).add(1, 'months').endOf('month').format('YYYY-MM-DD'),
  ];
  return apiGet(`api-pb/hotel-calendar/${urlParts.join('/')}/`)
    .then((stayResponse) => {
      const daysInfo = {};
      if (stayResponse && stayResponse.days) {
        stayResponse.days.forEach((dayData) => {
          daysInfo[dayData.day] = {
            available: dayData.available,
            stays: dayData.minimum_stay,
            amount: dayData.amount,
          };
        });
      }
      return daysInfo;
    }).catch(() => ({})); // returning an empty map instead
};

/** ************************************************************************** */
/* Devuelve la ficha de un hotel
/**************************************************************************** */
/*export const getHotelDetails = (hotelId) => ({
  type: 'BOOKING_GET_HOTEL_DETAILS',
  payload: {
    promise: apiGet(`api-pb/hotel/${hotelId}/`),
  },
});*/

// Acción con redux-thunk
export const getHotelDetails = (hotelId) => (dispatch) => apiGet(`api-pb/hotel/${hotelId}/`).then((response) => {
  dispatch({
    type: 'BOOKING_GET_HOTEL_DETAILS',
    payload: response.value, // Aquí puedes despachar los detalles del hotel
  });
  return response; // Asegúrate de retornar la respuesta para que pueda ser manejada en el then
}).catch((error) => {
  throw error; // Puedes manejar el error si es necesario
});

export const clearRoomSelection = (addEmptyCartEvent) => (dispatch, getState) => {
  const hasChangedHotel = (state) => {
    const { cartHotel } = state.cart;
    if (cartHotel.isEmpty) {
      return false; // the cart is already empty
    }
    const searchHotel = state.bookingFormState.destination || { code: 'searchHotel' };
    return searchHotel.code !== cartHotel.code;
  };

  dispatch(setMultipleRoomSelection(null));
  dispatch(setMultipleRoomStep(MULTIPLE_ROOMS.FIRST));

  const state = getState();
  const { isFreeModeSelected } = state.bookingFormState;
  if (isFreeModeSelected && hasChangedHotel(state)) {
    dispatch(emptyShoppingCart());
  } else if (addEmptyCartEvent) {
    // Even if internally we do not clear the cart, we still need to send the GA event
    dispatch(emptyShoppingCartEvent());
  }
};

/** ************************************************************************** */
/* Acción para descargar bono
/**************************************************************************** */
export const downloadVoucher = ({ locator, lang, is_agent_voucher }) => (dispatch) => {
  dispatch({ type: 'BOOKING_DOWNLOAD_VOUCHER' });

  return apiGet(`api-pb/voucher/pdf/${lang}/${locator}/?is_agent_voucher=${is_agent_voucher}`, lang, { isBlob: true })
    .then((response) => {
      const blob = new Blob([response], { type: 'application/pdf' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `${locator}.pdf`;
      link.click();
    });
};

/** ************************************************************************** */
/* Acción de cancelar una reserva
/**************************************************************************** */
export const cancel = (
  locator,
  hotelId,
  email,
  reason,
  childReason,
  reasonFreeText,
  customer_code,
  action,
) => (dispatch, getState) => {
  dispatch({ type: 'BOOKING_CANCEL' });
  const state = getState();
  const cancellation_reason = childReason || reason;
  const data = {
    reason: reasonFreeText,
    cancellation_reason,
    locator,
    hotel: hotelId,
    language: state.i18nState.lang,
    email,
    customer_code,
    action,
  };
  return apiPost('api-pb/cancellation/', data, data.language);
};

/** ***************************************************************************** */
/* Solicita a la API que se guarden los detalles del carrito para poder compartir
/******************************************************************************** */
export const updateCartShare = (language) => (dispatch, getState) => {
  dispatch({ type: 'BOOKING_UPDATE_CART_SHARE' });
  const { sessionId } = getState().bookingFormState;
  const stateForAgentCode = store.getState();
  const agentCodeEnteredInBookingForm = stateForAgentCode.booking.agentCode ? stateForAgentCode.booking.agentCode : '-';
  const rateTypeEnteredInBookingForm = stateForAgentCode.bookingFormState.rateType
    ? stateForAgentCode.bookingFormState.rateType : 'PUBLIC_RATE';
  const agentSellEnteredInBookingForm = stateForAgentCode.booking.agentSell
    ? stateForAgentCode.booking.agentSell : 'false';
  const agentUseCoinsEnteredInBookingForm = stateForAgentCode.bookingFormState.agentUseCoins
    ? stateForAgentCode.bookingFormState.agentUseCoins : 'true';

  return apiGet(`api-pb/shopping-cart/share/${language}/${sessionId}/${agentCodeEnteredInBookingForm}/`
    + `${rateTypeEnteredInBookingForm}/${agentSellEnteredInBookingForm}/`
    + `${agentUseCoinsEnteredInBookingForm}/`).then((response) => (
    response && response.token ? response.token : ''
  ));
};

/** ***************************************************************************** */
/* Recupera la información del carrito asociada a un enlace recibido por e-mail
/******************************************************************************** */
export const getCartShare = (token, language) => (dispatch, getState) => {
  dispatch({ type: 'BOOKING_LOAD_CART_SHARE' });
  const { sessionId } = getState().bookingFormState;
  return apiGet(`api-pb/shopping-cart/get-share/${sessionId}/${token}/`, language);
};

/** ***************************************************************************** */
/* Envía por e-mail una URL con la cual se pueden ver los mismos datos de disponibilidad
/******************************************************************************** */
export const sendCartShareEmail = (token, emails, roomAmounts, totalAmount) => (dispatch) => {
  dispatch({ type: 'BOOKING_CART_SHARE_EMAIL' });
  return apiPost(`api-pb/shopping-cart/cart-share/send/${token}/`,
    { emails, room_amounts: roomAmounts, total_amount: totalAmount });
};

/** ***************************************************************************** */
/* Envía por e-mail un budget a partir del cual se pueden recuperar los mismos datos de disponibilidad
/******************************************************************************** */
export const sendBudgetEmail = (payload) => (dispatch, getState) => {
  dispatch({ type: 'BOOKING_BUDGET_EMAIL' });
  const { sessionId } = getState().bookingFormState;
  const stateForAgentCode = store.getState();
  const agentCodeEnteredInBookingForm = stateForAgentCode.booking.agentCode ? stateForAgentCode.booking.agentCode : '-';
  const rateTypeEnteredInBookingForm = stateForAgentCode.bookingFormState.rateType
    ? stateForAgentCode.bookingFormState.rateType : 'PUBLIC_RATE';
  const agentSellEnteredInBookingForm = stateForAgentCode.booking.agentSell
    ? stateForAgentCode.booking.agentSell : 'false';
  const agentUseCoinsEnteredInBookingForm = stateForAgentCode.bookingFormState.agentUseCoins
    ? stateForAgentCode.bookingFormState.agentUseCoins : 'true';

  return apiPost(`api-pb/budget/send/${payload.lang}/${sessionId}/${agentCodeEnteredInBookingForm}/`
  + `${rateTypeEnteredInBookingForm}/${agentSellEnteredInBookingForm}/${agentUseCoinsEnteredInBookingForm}/`,
  {
    name: payload.name, email: payload.email, total_price: payload.totalPrice, loyalty: payload.isLoyalty,
  });
};

/** ***************************************************************************** */
/* Envía por e-mail una URL con la cual se pueden ver los mismos datos de disponibilidad
/******************************************************************************** */
export const sendAvailShareEmail = (url, emails, hotelCode, dates, nights, dist) => (dispatch) => {
  dispatch({ type: 'BOOKING_DISPO_SHARE_EMAIL' });
  return apiPost('api-pb/shopping-cart/cart-share/send-url/', {
    emails, url, hotel_dinguscode: hotelCode, dates, nights, dist,
  });
};

/** ***************************************************************************** */
/* Envía por e-mail un budget a partir del cual se pueden recuperar los mismos datos de disponibilidad
/******************************************************************************** */
export const retrieveBudgetEmail = (token) => (dispatch, getState) => {
  dispatch({ type: 'BOOKING_RETRIEVE_BUDGET_EMAIL' });
  const { sessionId } = getState().bookingFormState;
  return apiGet(`api-pb/budget/get/${sessionId}/${token}/`);
};

/** ********************************************************************************* */
/* Comprueba si un id de cliente es válido en la plataforma de fidelización de Sandos
/************************************************************************************ */
export const checkCustomerValid = (code) => (dispatch) => {
  dispatch({ type: 'BOOKING_CHECK_CUSTOMER_VALID' });
  return apiGet(`api-pb/check-customer-valid/${code}/`);
};

/** ********************************************************************************* */
/* Recupera el catálogo de aerolíneas de SeekAndGo (id + name), usado por el selector
/* de aerolíneas del formulario de transfer aeropuerto-hotel (ExtraTransfer.js)
/************************************************************************************ */
export const getAirlines = () => (dispatch) => {
  dispatch({ type: 'BOOKING_GET_AIRLINES' });
  return apiGet('api-pb/airlines/')
    .then((response) => (response && response.airlines) || [])
    .catch(() => []);
};

/** ************************************************************************** */
/* Envío de un mensaje de contacto al hotel. Relacionado con una reserva
/**************************************************************************** */
export const sendBookingMessage = (locator, email, message) => (dispatch) => {
  dispatch({ type: 'BOOKING_SEND_BOOKING_MESSAGE' });
  return apiPost(`api-pb/booking-message/${email}/`, {
    message,
    locator,
  });
};

/** ************************************************************************** */
/* Solicitud para enviar un email con el enlace a la página de mi reserva
/**************************************************************************** */
export const sendBookingDetailLink = (locator, ownerEmail, email, lang) => (dispatch) => {
  dispatch({ type: 'BOOKING_SEND_BOOKING_DETAIL_LINK' });
  return apiPost('api-pb/send-booking-detail-link/', {
    locator,
    email,
    owner_email: ownerEmail,
  }, lang);
};

/** ******************************************************************************* */
/* Permite modificar los nombres de los huéspedes de una habitaciones de la reserva
/********************************************************************************* */
export const updateRoomGuests = ({
  locator, email, roomIndex, occupants,
}) => (dispatch) => {
  dispatch({ type: 'BOOKING_ONE_ROOM_OCCUPANCY_UPDATE' });

  const body = {
    locator,
    email,
    room_index: roomIndex,
    occupancy: occupants,
  };
  // Send all the requests and verify if all of them were successful
  return apiPost('api-pb/room-occupancy-update/', body).then((successMessage) => ({ successMessage, body }));
};

/** ******************************************************************************* */
/* Permite modificar los nombres de los huéspedes de las habitaciones de la reserva
/********************************************************************************* */
export const updateBookingGuests = ({
  locator, email, roomsGuests, defaultName,
}) => (dispatch) => {
  dispatch({ type: 'BOOKING_ROOM_OCCUPANCY_UPDATE' });
  const guestName = (guest) => {
    const tokens = [guest.name || '', guest.surname || '', guest.surname2 || ''];
    return tokens.join(' ').trim() || defaultName;
  };
  const requests = roomsGuests.map((roomGuests, roomIndex) => {
    const body = {
      locator,
      email,
      room_index: roomIndex,
      occupancy: roomGuests.map((roomGuest) => ({
        age: roomGuest.age,
        ptype: roomGuest.ptype,
        name: guestName(roomGuest),
        code: roomGuest.code,
      })),
    };
    return apiPost('api-pb/room-occupancy-update/', body);
  });

  // Send all the requests and verify if all of them were successful
  return Promise.all(requests).then((responses) =>
    responses.find((response) => !response || !response.update_ok) !== -1,
  );
};

/** ******************************************************************************* */
/* Recibe los longstays de un Hotel.
/********************************************************************************* */
export const loadLongStayBanner = (hotelCode, checkin, checkout) => (dispatch) => {
  const url = `api-pb/longstay/check/${hotelCode}/${checkin}/${checkout}/`;
  return apiGet(url).then((response) => {
    const stayInfo = response.longstay;
    if (stayInfo && stayInfo.discount) {
      const gtmEvent = {
        event: 'longStayApplies',
        hotel: hotelCode,
        fechaDeInicio: checkin,
        fechaDeSalida: checkout,
      };
      dispatch(sendGTMEvents([gtmEvent]));
      const banner = {
        type: 'LONG_STAY',
        fromDate: stayInfo.initial_date,
        toDate: stayInfo.end_date || undefined,
        discount: stayInfo.discount,
      };
      dispatch({ type: 'HOTEL_GET_LONGSTAYS', banner });
    }
  });
};

export const validateEmailToEmailValidator = (email, validation_enabled) => () => {
  if (validation_enabled === true) {
    // Used by the Customer to pay upfront for his Refundable booking before arriving to the hotel
    return apiPost('api-pb/email-validator/validate/',
      { email }).then((validateEmailResponse) => validateEmailResponse.valid).catch(() => ({ valid: false }));
  }
  return { valid: true };
};

export const processIpClientCountryCode = () => async (dispatch, getState) => {
  const state = getState();
  try {
    const { clientIpCountryCode } = await getClientIpCountryCode(state);
    // Guardamos el clientIpCountryCode
    dispatch(setClientIpCountryCode(clientIpCountryCode));
  } catch {
    dispatch(setClientIpCountryCode(null));
  }
};

export const getClientIpCountryCode = async (state) => {
  const { lang } = state.i18nState;
  const data = await apiGet('api-pb/client-ip-country-code/', lang);
  return ({
    clientIpCountryCode: data.client_ip_country_code,
  });
};
